import React, { useMemo, useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Button, Dropdown, Form } from 'semantic-ui-react';
import { Popup } from '../../lib/custom-ui';

import { useForm } from '../../hooks';
import actions from '../../actions';
import selectors from '../../selectors';

import styles from './CardMoveStep.module.scss';

const CardMoveStep = React.memo(
  ({ projectsToLists, defaultPath, onMove, onTransfer, onBoardFetch, onBack, onClose }) => {
    const [t] = useTranslation();
    const dispatch = useDispatch();
    const transferStatus = useSelector(selectors.selectCardTransferStatus);
    const [localError, setLocalError] = useState(null);

    const [path, handleFieldChange] = useForm(() => ({
      projectId: null,
      boardId: null,
      listId: null,
      ...defaultPath,
    }));

    const selectedProject = useMemo(
      () =>
        projectsToLists.find((project) => String(project.id) === String(path.projectId)) || null,
      [projectsToLists, path.projectId],
    );

    const selectedBoard = useMemo(
      () =>
        (selectedProject &&
          selectedProject.boards.find((board) => String(board.id) === String(path.boardId))) ||
        null,
      [selectedProject, path.boardId],
    );

    const selectedList = useMemo(
      () =>
        (selectedBoard &&
          selectedBoard.lists.find((list) => String(list.id) === String(path.listId))) ||
        null,
      [selectedBoard, path.listId],
    );

    const isSubmitting = transferStatus?.status === 'pending';
    const errorMessage =
      localError ||
      (transferStatus?.status === 'failure' ? transferStatus.message : null);

    useEffect(() => {
      dispatch(actions.clearCardTransferStatus());
      return () => {
        dispatch(actions.clearCardTransferStatus());
      };
    }, [dispatch]);

    useEffect(() => {
      if (!transferStatus) {
        return;
      }

      if (transferStatus.status === 'success') {
        onClose();
      }
    }, [transferStatus, onClose]);

    const handleBoardIdChange = useCallback(
      (event, data) => {
        setLocalError(null);
        if (transferStatus?.status === 'failure') {
          dispatch(actions.clearCardTransferStatus());
        }

        const board = selectedProject.boards.find(
          (item) => String(item.id) === String(data.value),
        );
        if (board && board.isFetching === null) {
          onBoardFetch(data.value);
        }

        handleFieldChange(event, data);
      },
      [
        onBoardFetch,
        handleFieldChange,
        selectedProject,
        transferStatus,
        dispatch,
      ],
    );

    const handleFieldChangeWithClear = useCallback(
      (event, data) => {
        setLocalError(null);
        if (transferStatus?.status === 'failure') {
          dispatch(actions.clearCardTransferStatus());
        }
        handleFieldChange(event, data);
      },
      [handleFieldChange, transferStatus, dispatch],
    );

    const handleSubmit = useCallback(() => {
      if (isSubmitting) {
        return;
      }

      setLocalError(null);
      if (transferStatus?.status === 'failure') {
        dispatch(actions.clearCardTransferStatus());
      }

      if (!selectedBoard || !selectedList) {
        setLocalError('Выберите список для перемещения');
        return;
      }

      const isSameBoard = String(selectedBoard.id) === String(defaultPath.boardId);
      const isSameList = String(selectedList.id) === String(defaultPath.listId);

      if (isSameBoard && isSameList) {
        setLocalError('Карточка уже находится в выбранном списке');
        return;
      }

      if (!isSameBoard) {
        onTransfer(selectedBoard.id, selectedList.id);
        return;
      }

      onMove(selectedList.id);
    }, [
      isSubmitting,
      transferStatus,
      dispatch,
      selectedBoard,
      selectedList,
      defaultPath,
      onMove,
      onTransfer,
    ]);

    const isBoardLoading = Boolean(selectedBoard && selectedBoard.isFetching !== false);
    const canSubmit = Boolean(selectedList) && !isBoardLoading && !isSubmitting;

    return (
      <>
        <Popup.Header onBack={onBack}>
          {t('common.moveCard', {
            context: 'title',
          })}
        </Popup.Header>
        <Popup.Content>
          <Form onSubmit={handleSubmit}>
            <div className={styles.text}>{t('common.project')}</div>
            <Dropdown
              fluid
              selection
              name="projectId"
              options={projectsToLists.map((project) => ({
                text: project.name,
                value: project.id,
              }))}
              value={selectedProject && selectedProject.id}
              placeholder={
                projectsToLists.length === 0 ? t('common.noProjects') : t('common.selectProject')
              }
              disabled={projectsToLists.length === 0 || isSubmitting}
              className={styles.field}
              onChange={handleFieldChangeWithClear}
            />
            {selectedProject && (
              <>
                <div className={styles.text}>{t('common.board')}</div>
                <Dropdown
                  fluid
                  selection
                  name="boardId"
                  options={selectedProject.boards.map((board) => ({
                    text: board.name,
                    value: board.id,
                  }))}
                  value={selectedBoard && selectedBoard.id}
                  placeholder={
                    selectedProject.boards.length === 0
                      ? t('common.noBoards')
                      : t('common.selectBoard')
                  }
                  disabled={selectedProject.boards.length === 0 || isSubmitting}
                  className={styles.field}
                  onChange={handleBoardIdChange}
                />
              </>
            )}
            {selectedBoard && (
              <>
                <div className={styles.text}>{t('common.list')}</div>
                <Dropdown
                  fluid
                  selection
                  name="listId"
                  options={selectedBoard.lists.map((list) => ({
                    text: list.name,
                    value: list.id,
                  }))}
                  value={selectedList && selectedList.id}
                  placeholder={
                    selectedBoard.isFetching === false && selectedBoard.lists.length === 0
                      ? t('common.noLists')
                      : t('common.selectList')
                  }
                  loading={isBoardLoading}
                  disabled={isBoardLoading || selectedBoard.lists.length === 0 || isSubmitting}
                  className={styles.field}
                  onChange={handleFieldChangeWithClear}
                />
              </>
            )}
            {errorMessage && <div className={styles.errorMessage}>{errorMessage}</div>}
            <Button
              positive
              content={t('action.move')}
              loading={isSubmitting}
              disabled={!canSubmit}
            />
          </Form>
        </Popup.Content>
      </>
    );
  },
);

CardMoveStep.propTypes = {
  /* eslint-disable react/forbid-prop-types */
  projectsToLists: PropTypes.array.isRequired,
  defaultPath: PropTypes.object.isRequired,
  /* eslint-enable react/forbid-prop-types */
  onMove: PropTypes.func.isRequired,
  onTransfer: PropTypes.func.isRequired,
  onBoardFetch: PropTypes.func.isRequired,
  onBack: PropTypes.func,
  onClose: PropTypes.func.isRequired,
};

CardMoveStep.defaultProps = {
  onBack: undefined,
};

export default CardMoveStep;
